import { Link } from 'react-router-dom';
import { useEffect } from 'react';

export default function TermsOfService() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-sm border-b border-yellow-500/20">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <i className="ri-arrow-left-line text-xl text-yellow-500"></i>
            <span className="text-lg font-semibold">Back to Home</span>
          </Link>
          <div className="text-yellow-500 font-bold text-xl">Amagi Poker</div>
        </div>
      </nav>

      {/* Content */}
      <div className="pt-24 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-yellow-500 to-yellow-300 bg-clip-text text-transparent">
              Terms of Service
            </h1>
            <p className="text-gray-400">Last Updated: January 2025</p>
          </div>

          {/* Content Sections */}
          <div className="space-y-8">
            {/* Agreement */}
            <section className="bg-gradient-to-br from-yellow-500/5 to-yellow-600/5 rounded-2xl p-8 border border-yellow-500/20">
              <h2 className="text-2xl font-bold text-yellow-500 mb-4">1. Agreement to Terms</h2>
              <p className="text-gray-300 leading-relaxed">
                By accessing or using Amagi Poker (the "Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, you may not access or use the Service. These Terms apply to all users, including visitors, registered users, and any other individuals who access the Service.
              </p>
            </section>

            {/* Service Description */}
            <section className="bg-gradient-to-br from-yellow-500/5 to-yellow-600/5 rounded-2xl p-8 border border-yellow-500/20">
              <h2 className="text-2xl font-bold text-yellow-500 mb-4">2. Service Description</h2>
              <div className="space-y-3 text-gray-300">
                <p className="leading-relaxed">Amagi Poker is a mobile poker gaming application that provides:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Interactive poker gameplay with virtual chips</li>
                  <li>Multiplayer gaming experiences</li>
                  <li>Social features including chat and friend systems</li>
                  <li>In-app purchases for virtual currency and items</li>
                  <li>Leaderboards and achievement systems</li>
                </ul>
                <p className="leading-relaxed mt-4">
                  We reserve the right to modify, suspend, or discontinue any aspect of the Service at any time without prior notice.
                </p>
              </div>
            </section>

            {/* User Eligibility */}
            <section className="bg-gradient-to-br from-yellow-500/5 to-yellow-600/5 rounded-2xl p-8 border border-yellow-500/20">
              <h2 className="text-2xl font-bold text-yellow-500 mb-4">3. User Eligibility</h2>
              <div className="space-y-3 text-gray-300">
                <p className="leading-relaxed">To use the Service, you must:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Be at least 18 years of age or the legal age of majority in your jurisdiction</li>
                  <li>Have the legal capacity to enter into binding contracts</li>
                  <li>Not be prohibited from using the Service under applicable laws</li>
                  <li>Comply with all local laws regarding online gaming and gambling</li>
                  <li>Provide accurate and complete registration information</li>
                </ul>
                <p className="leading-relaxed mt-4 font-semibold text-white">
                  By using the Service, you represent and warrant that you meet all eligibility requirements.
                </p>
              </div>
            </section>

            {/* Account Responsibilities */}
            <section className="bg-gradient-to-br from-yellow-500/5 to-yellow-600/5 rounded-2xl p-8 border border-yellow-500/20">
              <h2 className="text-2xl font-bold text-yellow-500 mb-4">4. Account Registration and Responsibilities</h2>
              <div className="space-y-4 text-gray-300">
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">4.1 Account Creation</h3>
                  <p className="leading-relaxed">You must create an account to access certain features. You agree to provide accurate, current, and complete information during registration and to update such information to keep it accurate.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">4.2 Account Security</h3>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>You are responsible for maintaining the confidentiality of your account credentials</li>
                    <li>You are responsible for all activities that occur under your account</li>
                    <li>You must notify us immediately of any unauthorized access or security breach</li>
                    <li>You may not share, sell, or transfer your account to others</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">4.3 Account Termination</h3>
                  <p className="leading-relaxed">We reserve the right to suspend or terminate your account at any time for violation of these Terms or for any other reason at our sole discretion.</p>
                </div>
              </div>
            </section>

            {/* Prohibited Conduct */}
            <section className="bg-gradient-to-br from-yellow-500/5 to-yellow-600/5 rounded-2xl p-8 border border-yellow-500/20">
              <h2 className="text-2xl font-bold text-yellow-500 mb-4">5. Prohibited Conduct</h2>
              <div className="space-y-3 text-gray-300">
                <p className="leading-relaxed">You agree not to:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Use the Service for any illegal purpose or in violation of any laws</li>
                  <li>Cheat, exploit bugs, or use unauthorized third-party software</li>
                  <li>Harass, abuse, or harm other users</li>
                  <li>Impersonate any person or entity</li>
                  <li>Transmit viruses, malware, or other harmful code</li>
                  <li>Attempt to gain unauthorized access to the Service or other users' accounts</li>
                  <li>Engage in any form of automated data collection (scraping, bots, etc.)</li>
                  <li>Reverse engineer, decompile, or disassemble the Service</li>
                  <li>Use the Service for commercial purposes without authorization</li>
                  <li>Create multiple accounts to circumvent restrictions</li>
                  <li>Engage in money laundering or fraudulent activities</li>
                  <li>Collude with other players to gain unfair advantages</li>
                </ul>
              </div>
            </section>

            {/* Virtual Currency */}
            <section className="bg-gradient-to-br from-yellow-500/5 to-yellow-600/5 rounded-2xl p-8 border border-yellow-500/20">
              <h2 className="text-2xl font-bold text-yellow-500 mb-4">6. Virtual Currency and In-App Purchases</h2>
              <div className="space-y-4 text-gray-300">
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">6.1 Virtual Items</h3>
                  <p className="leading-relaxed">The Service may include virtual currency, chips, and other virtual items that can be purchased with real money. These virtual items have no real-world value and cannot be exchanged for cash or real-world goods.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">6.2 Purchases</h3>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>All purchases are final and non-refundable except as required by law</li>
                    <li>Prices are subject to change without notice</li>
                    <li>You are responsible for all charges incurred under your account</li>
                    <li>We are not responsible for unauthorized purchases made through your account</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">6.3 No Real Money Gaming</h3>
                  <p className="leading-relaxed font-semibold text-white">Amagi Poker is a social gaming platform. Virtual chips cannot be cashed out for real money. The Service does not constitute real money gambling.</p>
                </div>
              </div>
            </section>

            {/* Intellectual Property */}
            <section className="bg-gradient-to-br from-yellow-500/5 to-yellow-600/5 rounded-2xl p-8 border border-yellow-500/20">
              <h2 className="text-2xl font-bold text-yellow-500 mb-4">7. Intellectual Property Rights</h2>
              <div className="space-y-4 text-gray-300">
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">7.1 Our Rights</h3>
                  <p className="leading-relaxed">All content, features, and functionality of the Service, including but not limited to text, graphics, logos, icons, images, audio clips, video clips, data compilations, software, and the compilation thereof, are the exclusive property of Amagi Poker and are protected by copyright, trademark, and other intellectual property laws.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">7.2 Limited License</h3>
                  <p className="leading-relaxed">We grant you a limited, non-exclusive, non-transferable, revocable license to access and use the Service for personal, non-commercial purposes, subject to these Terms.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">7.3 User Content</h3>
                  <p className="leading-relaxed">By submitting content to the Service (such as chat messages, profile information, or feedback), you grant us a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, and display such content in connection with the Service.</p>
                </div>
              </div>
            </section>

            {/* Disclaimer */}
            <section className="bg-gradient-to-br from-yellow-500/5 to-yellow-600/5 rounded-2xl p-8 border border-yellow-500/20">
              <h2 className="text-2xl font-bold text-yellow-500 mb-4">8. Disclaimer of Warranties</h2>
              <div className="space-y-3 text-gray-300">
                <p className="leading-relaxed font-semibold text-white">THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED.</p>
                <p className="leading-relaxed">We disclaim all warranties, including but not limited to:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Merchantability and fitness for a particular purpose</li>
                  <li>Non-infringement of third-party rights</li>
                  <li>Uninterrupted, secure, or error-free operation</li>
                  <li>Accuracy, reliability, or completeness of content</li>
                  <li>Correction of defects or errors</li>
                </ul>
                <p className="leading-relaxed mt-4">Your use of the Service is at your sole risk. We do not warrant that the Service will meet your requirements or expectations.</p>
              </div>
            </section>

            {/* Limitation of Liability */}
            <section className="bg-gradient-to-br from-yellow-500/5 to-yellow-600/5 rounded-2xl p-8 border border-yellow-500/20">
              <h2 className="text-2xl font-bold text-yellow-500 mb-4">9. Limitation of Liability</h2>
              <div className="space-y-3 text-gray-300">
                <p className="leading-relaxed font-semibold text-white">TO THE MAXIMUM EXTENT PERMITTED BY LAW, AMAGI POKER SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES.</p>
                <p className="leading-relaxed">This includes damages arising from:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Use or inability to use the Service</li>
                  <li>Unauthorized access to your account or data</li>
                  <li>Loss of virtual currency or items</li>
                  <li>Errors, bugs, or interruptions in the Service</li>
                  <li>Conduct of other users</li>
                  <li>Any other matter relating to the Service</li>
                </ul>
                <p className="leading-relaxed mt-4">Our total liability shall not exceed the amount you paid to us in the 12 months preceding the claim, or $100, whichever is greater.</p>
              </div>
            </section>

            {/* Indemnification */}
            <section className="bg-gradient-to-br from-yellow-500/5 to-yellow-600/5 rounded-2xl p-8 border border-yellow-500/20">
              <h2 className="text-2xl font-bold text-yellow-500 mb-4">10. Indemnification</h2>
              <p className="text-gray-300 leading-relaxed">
                You agree to indemnify, defend, and hold harmless Amagi Poker, its affiliates, officers, directors, employees, and agents from any claims, liabilities, damages, losses, costs, or expenses (including reasonable attorneys&apos; fees) arising from: (a) your use of the Service; (b) your violation of these Terms; (c) your violation of any rights of another party; or (d) any content you submit to the Service.
              </p>
            </section>

            {/* Termination */}
            <section className="bg-gradient-to-br from-yellow-500/5 to-yellow-600/5 rounded-2xl p-8 border border-yellow-500/20">
              <h2 className="text-2xl font-bold text-yellow-500 mb-4">11. Termination</h2>
              <div className="space-y-3 text-gray-300">
                <p className="leading-relaxed">We may terminate or suspend your access to the Service immediately, without prior notice or liability, for any reason, including:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Breach of these Terms</li>
                  <li>Fraudulent or illegal activity</li>
                  <li>Prolonged inactivity</li>
                  <li>Request by law enforcement or government agencies</li>
                  <li>Discontinuation of the Service</li>
                </ul>
                <p className="leading-relaxed mt-4">Upon termination, your right to use the Service will immediately cease. All provisions of these Terms that by their nature should survive termination shall survive, including ownership provisions, warranty disclaimers, and limitations of liability.</p>
              </div>
            </section>

            {/* Governing Law */}
            <section className="bg-gradient-to-br from-yellow-500/5 to-yellow-600/5 rounded-2xl p-8 border border-yellow-500/20">
              <h2 className="text-2xl font-bold text-yellow-500 mb-4">12. Governing Law and Dispute Resolution</h2>
              <div className="space-y-4 text-gray-300">
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">12.1 Governing Law</h3>
                  <p className="leading-relaxed">These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which Amagi Poker operates, without regard to conflict of law provisions.</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white mb-2">12.2 Dispute Resolution</h3>
                  <p className="leading-relaxed">Any disputes arising from these Terms or the Service shall be resolved through binding arbitration, except that either party may seek injunctive relief in court for intellectual property infringement or unauthorized access.</p>
                </div>
              </div>
            </section>

            {/* Changes to Terms */}
            <section className="bg-gradient-to-br from-yellow-500/5 to-yellow-600/5 rounded-2xl p-8 border border-yellow-500/20">
              <h2 className="text-2xl font-bold text-yellow-500 mb-4">13. Changes to Terms</h2>
              <p className="text-gray-300 leading-relaxed">
                We reserve the right to modify these Terms at any time. We will notify you of material changes by posting the updated Terms on this page and updating the "Last Updated" date. Your continued use of the Service after such changes constitutes acceptance of the modified Terms. If you do not agree to the modified Terms, you must stop using the Service.
              </p>
            </section>

            {/* Miscellaneous */}
            <section className="bg-gradient-to-br from-yellow-500/5 to-yellow-600/5 rounded-2xl p-8 border border-yellow-500/20">
              <h2 className="text-2xl font-bold text-yellow-500 mb-4">14. Miscellaneous</h2>
              <div className="space-y-3 text-gray-300">
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong className="text-white">Entire Agreement:</strong> These Terms constitute the entire agreement between you and Amagi Poker regarding the Service.</li>
                  <li><strong className="text-white">Severability:</strong> If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall remain in full force and effect.</li>
                  <li><strong className="text-white">Waiver:</strong> No waiver of any term shall be deemed a further or continuing waiver of such term or any other term.</li>
                  <li><strong className="text-white">Assignment:</strong> You may not assign or transfer these Terms without our prior written consent. We may assign these Terms without restriction.</li>
                  <li><strong className="text-white">Force Majeure:</strong> We shall not be liable for any failure to perform due to circumstances beyond our reasonable control.</li>
                </ul>
              </div>
            </section>
          </div>

          {/* Back to Top */}
          <div className="mt-12 text-center">
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="inline-flex items-center gap-2 px-6 py-3 bg-yellow-500 text-black font-semibold rounded-full hover:bg-yellow-400 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-up-line"></i>
              Back to Top
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
